/**
 * Shared shape-morph primitives for the brand's rounded -> faceted button
 * shapes. Used by the btn-cta, btn-pill, btn-icon, and custom-select
 * modules.
 *
 * Driven entirely by a native `clip-path: path()` transition rather than a
 * JS tween: the browser interpolates two `path()` values itself, including
 * genuine elastic overshoot (see --ease-elastic in base/animation.css),
 * provided both paths have the same command sequence. `path()` can't run
 * arithmetic on its own coordinates though - a wider button needs a wider
 * rest/hover pair, a bigger icon slot needs a bigger circle/heptagon pair -
 * so this still measures each shape's element and hands the two finished
 * path strings to CSS as --rest-clip/--hover-clip custom properties. The
 * :hover swap, the transition, and the easing are plain CSS (see
 * components/button.css and components/custom-select.css).
 */

export const TEXT_HEIGHT = 42
export const ICON_BASE_SIZE = 44

// A point on a cubic-bezier path, carrying its own in/out control handles so
// each segment can independently be a true curve or a degenerate (dead
// straight) line depending on how far apart its handles are from the anchor.
const curvePoint = (anchor, inHandle, outHandle) => ({
    anchor,
    in: inHandle ?? anchor,
    out: outHandle ?? anchor,
})
const straightCurvePoint = (anchor) => curvePoint(anchor)

const curvePointsToPath = (points) => {
    const segments = points.map((p, i) => {
        const next = points[(i + 1) % points.length]
        return `C${p.out.join(',')} ${next.in.join(',')} ${next.anchor.join(',')}`
    })
    return `path('M${points[0].anchor.join(',')}${segments.join('')}Z')`
}

const arcAnchor = (cx, cy, angleDeg, radius) => {
    const rad = (angleDeg * Math.PI) / 180
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)]
}

// Unit tangent at a point on the circle, in the direction both caps are
// swept below (decreasing angle).
const travelTangent = (angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180
    return [Math.sin(rad), -Math.cos(rad)]
}

// Standard cubic-bezier circle approximation: for an arc segment spanning
// `stepDeg`, offsetting each anchor's control handle this far along the
// tangent reproduces the true circle to a very close approximation.
const bezierHandleLength = (stepDeg, radius) =>
    radius * (4 / 3) * Math.tan((stepDeg * Math.PI) / 180 / 4)

const LEFT_ANGLES = [270, 240, 210, 180, 150, 120, 90]
const RIGHT_ANGLES = [90, 64.2857, 38.5714, 12.8571, -12.8571, -38.5714, -64.2857, -90]

// Points taken directly from the brand's abstract-shape SVG export, with the
// right side re-anchored to the element's current width instead of a fixed one.
const labelHoverPoints = (width, height = TEXT_HEIGHT) =>
    [
        [8.59091, 0.00123],
        [6.8874, 0.00125],
        [3.81818, 8.59091],
        [0, 17.6591],
        [0, 37.7045],
        [6.84374, 41.9755],
        [8.59091, 42],
        [width - 9.408, 42],
        [width - 7.624, 42],
        [width - 5.03, 37.2273],
        [width - 2.059, 27.6818],
        [width - 0.408, 21],
        [width - 0.408, 4.29546],
        [width - 7.578, 0.0244598],
        [width - 9.408, 0],
    ].map(([x, y]) => [x, (y * height) / TEXT_HEIGHT])

// hover's left region is exactly 7 points and its right region exactly 8 (an
// asymmetry inherited from the brand's hand-drawn export), so both caps are
// parametrized as true semicircles sharing those same point counts - no
// automatic point-matching to go wrong. This is hand-matched on purpose: an
// automatic correspondence between a smooth rest stadium and this sharp,
// asymmetric hover shape risks a visible fold artifact at the seam (points
// getting matched out of spatial order), which hand-picking avoids entirely.
//
// Each arc point gets real bezier control handles along the circle's
// tangent, so the caps render as true curves - except the handles facing the
// stretchy middle's flat top/bottom edges, which stay degenerate so those
// two segments remain straight lines right up to where the arc begins.
const labelRestCurvePoints = (width, height = TEXT_HEIGHT) => {
    const radius = height / 2 // true semicircle - both ends stay fully rounded at any height

    const buildCap = (cx, angles) => {
        const k = bezierHandleLength(Math.abs(angles[1] - angles[0]), radius)
        return angles.map((angle) => {
            const anchor = arcAnchor(cx, radius, angle, radius)
            const tangent = travelTangent(angle)
            const out = [anchor[0] + k * tangent[0], anchor[1] + k * tangent[1]]
            const inHandle = [anchor[0] - k * tangent[0], anchor[1] - k * tangent[1]]
            return curvePoint(anchor, inHandle, out)
        })
    }

    const left = buildCap(radius, LEFT_ANGLES)
    const right = buildCap(width - radius, RIGHT_ANGLES)

    left[0].in = left[0].anchor // flat top edge (wraps from right's last point)
    left[left.length - 1].out = left[left.length - 1].anchor // flat bottom edge
    right[0].in = right[0].anchor // flat bottom edge
    right[right.length - 1].out = right[right.length - 1].anchor // flat top edge

    return [...left, ...right]
}

const labelHoverCurvePoints = (width, height = TEXT_HEIGHT) =>
    labelHoverPoints(width, height).map(straightCurvePoint)

const labelRestClip = (width, height = TEXT_HEIGHT) =>
    curvePointsToPath(labelRestCurvePoints(width, height))
const labelHoverClip = (width, height = TEXT_HEIGHT) =>
    curvePointsToPath(labelHoverCurvePoints(width, height))

// Icon hover shape, authored at a 44px base size and scaled to whatever size
// the icon slot actually measures - same idea as the label's width-only
// stretch, just uniform since the icon slot is square.
const ICON_HOVER_POINTS_BASE = [
    [0.064, 12.512],
    [0.064, 32.576],
    [9.504, 42],
    [29.104, 45.008],
    [45.056, 32.576],
    [42.176, 12.512],
    [21.072, 0],
]

const iconHoverPoints = (size) => {
    const scale = size / ICON_BASE_SIZE
    return ICON_HOVER_POINTS_BASE.map(([x, y]) => [x * scale, y * scale])
}

// The hover heptagon's 7 vertices sweep the icon's centre in one consistent
// rotational direction, from roughly 203deg down to -92deg. Starting the
// rest circle's own 7 points at that same angle and stepping by the same
// 360/7 keeps each rest point already near where its hover partner sits, so
// the morph doesn't twist - the same hand-matching idea as the label shape,
// just a full circle instead of two semicircle caps.
const ICON_START_ANGLE = 203.39

const iconRestCurvePoints = (size) => {
    const radius = size / 2
    const step = 360 / ICON_HOVER_POINTS_BASE.length
    const k = bezierHandleLength(step, radius)

    return ICON_HOVER_POINTS_BASE.map((_, i) => {
        const angle = ICON_START_ANGLE - i * step
        const anchor = arcAnchor(radius, radius, angle, radius)
        const tangent = travelTangent(angle)
        const out = [anchor[0] + k * tangent[0], anchor[1] + k * tangent[1]]
        const inHandle = [anchor[0] - k * tangent[0], anchor[1] - k * tangent[1]]
        return curvePoint(anchor, inHandle, out)
    })
}

const iconHoverCurvePoints = (size) => iconHoverPoints(size).map(straightCurvePoint)

const iconRestClip = (size) => curvePointsToPath(iconRestCurvePoints(size))
const iconHoverClip = (size) => curvePointsToPath(iconHoverCurvePoints(size))

// Every shape element is `absolute inset-0` inside a `position: relative`
// container (its own direct parent, by this component pattern's convention),
// so its own rendered box is already exactly the box we need to build the
// clip-path coordinates against - no separate measure element required.
const initShape = (shapeEl, restClip, hoverClip) => {
    const rebuild = () => {
        const rect = shapeEl.getBoundingClientRect()
        const width = Math.round(rect.width)
        // Buttons given an explicit height (e.g. the 60px mobile-menu pills)
        // need the shape rebuilt at that height too, or it stays 42 tall and
        // renders centred with gaps above and below.
        const height = Math.round(rect.height) || TEXT_HEIGHT
        if (!width) return

        // --rest-clip/--hover-clip changing is what the CSS `transition:
        // clip-path` reacts to, so without suppressing it here the very
        // first measurement (page load) would visibly morph from the CSS
        // fallback shape to the real one, and every later resize would
        // replay the wobble too. Only a genuine :hover/.cursor-follow/
        // .--active state change should ever animate.
        shapeEl.style.transition = 'none'
        shapeEl.style.setProperty('--rest-clip', restClip(width, height))
        shapeEl.style.setProperty('--hover-clip', hoverClip(width, height))
        void shapeEl.offsetWidth // force layout so transition:none above actually commits
        shapeEl.style.transition = ''
    }

    new ResizeObserver(rebuild).observe(shapeEl)
    rebuild()

    // Exposed because a shape measured while its container is display:none
    // (behind [x-cloak], or a d:hidden mobile menu) bails out at 0 width and
    // never gets its custom properties set. Callers that reveal such a
    // container can force the measurement once it actually has layout.
    return { rebuild }
}

export const initLabelShapeMorph = (shapeEl) => initShape(shapeEl, labelRestClip, labelHoverClip)

export const initIconShapeMorph = (shapeEl) => initShape(shapeEl, iconRestClip, iconHoverClip)
